import * as React from "react";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import Button from "@material-ui/core/Button";

import { ConnectedSheet } from "../sheet";
import { ConnectedListSection } from "./list-section";
import { ConnectedInquiry } from "./inquiry";
import { StoreState } from "../../reducers/root-reducer";
import { setDateToMidnight, format, formatDate } from "../../util/date-utility";
import { ConnectedBookingPlanDrawer } from "./booking-plan-drawer";
import { setDate } from "../../actions/store/app-actions";
import { setActiveArea } from "../../actions/store/overview-actions";
import { Area } from "../../api/areas";
import { Inquiry } from "../../api/inquiries";
import { ConnectedSendMessageDialog } from "./send-message-dialog";
import { addInquiryMessageAndUpdateStore } from "../../actions/inquiry-actions";
import { ConnectedBookingListDialog } from "./booking-list-dialog";
import classNames from "classnames";
import { InjectedRouterProps } from "../../router/props";
import { Routes } from "../../router/routes";
import { withRouter } from "react-router-dom";
import IconButton from "@material-ui/core/IconButton";
import AssessmentIcon from "@material-ui/icons/AssessmentOutlined";
import ListIcon from "@material-ui/icons/List";

interface State {
	groupedInquiries: {
		[key: number]: {
			default: Inquiry[];
			snoozed: Inquiry[];
		};
	};
	showBookingPlan: boolean;
	replyingInquiry: Inquiry;
}

export interface InquiryListProps {}

interface ConnectedInquiryListProps extends WithStyles<typeof styles> {
	areas: Area[];
	inquiries: Inquiry[];
	archivedInquiries: Inquiry[];
	archivedInquiriesVisible: boolean;
	dateRange: {
		startDate: Date;
		endDate: Date;
	};
	selectedDate: Date;
	activeAreaId: string;
	highlightedInquiry: string;
	layout: "desktop" | "tablet" | "phone";
	setDate: (date: Date) => void;
	setActiveArea: (id: string) => void;
}

type Props = InquiryListProps & ConnectedInquiryListProps & InjectedRouterProps;

export class InquiryList extends React.Component<Props, State> {
	public state: State = {
		groupedInquiries: {},
		showBookingPlan: false,
		replyingInquiry: null
	};

	private lastSelectedDate: Date = null;
	private lastActiveAreaId: string = null;

	public componentDidMount() {
		this.groupInquiriesByDate(this.props);
	}

	public componentWillReceiveProps(nextProps: Props) {
		this.groupInquiriesByDate(nextProps);
	}

	private groupInquiriesByDate = (props: Props) => {
		const { startDate, endDate } = props.dateRange;
		const groupedInquiries: {
			[key: number]: {
				default: Inquiry[];
				snoozed: Inquiry[];
			};
		} = {};

		const sortedInquiries = props.archivedInquiriesVisible ? [...props.archivedInquiries] : [...props.inquiries];
		sortedInquiries.sort((a, b) => a.date.getTime() - b.date.getTime());

		sortedInquiries.forEach(inquiry => {
			if (
				((!inquiry.rejected && !props.archivedInquiriesVisible) || 
					(inquiry.rejected && props.archivedInquiriesVisible)) &&
				!inquiry.deleted &&
				!inquiry.moved &&
				!inquiry.accepted &&
				(!startDate ||
					!endDate ||
					(inquiry.date >= startDate && inquiry.date <= endDate))
			) {
				const day = setDateToMidnight(inquiry.date);
				const key = day.getTime();

				if (!groupedInquiries[key]) {
					groupedInquiries[key] = {
						default: [],
						snoozed: []
					};
				}

				if (inquiry.snoozed) {
					groupedInquiries[key].snoozed.push(inquiry);
				} else {
					groupedInquiries[key].default.push(inquiry);
				}
			}
		});

		this.setState({ groupedInquiries });
	};

	private handleShowDashboardButtonClick = (ts: string) => {
		const { history, setDate } = this.props;

		const date = new Date(parseInt(ts));
		date.setHours(0, 0, 0, 0);
		setDate(date);
		
		history.push(Routes.Dashboard);
	}

	private handleShowBookingPlanButtonClick = (ts: string) => {
		const {
			areas,
			selectedDate,
			activeAreaId,
			setDate,
			setActiveArea
		} = this.props;

		const date = new Date(parseInt(ts));
		date.setHours(0, 0, 0, 0);

		this.lastSelectedDate = new Date(selectedDate.getTime());
		this.lastActiveAreaId = activeAreaId;

		setDate(date);
		setActiveArea(areas[0].id);

		this.setState({
			showBookingPlan: true
		});
	};

	private handleBookingPlanDrawerClose = () => {
		const { setDate, setActiveArea } = this.props;

		setTimeout(() => {
			if (this.lastSelectedDate) {
				setDate(this.lastSelectedDate);
			}

			if (this.lastActiveAreaId) {
				setActiveArea(this.lastActiveAreaId);
			}

			this.lastSelectedDate = null;
			this.lastActiveAreaId = null;
		}, 225);

		this.setState({
			showBookingPlan: false
		});
	};

	private handleReply = (inquiry: Inquiry) => {
		this.setState({
			replyingInquiry: inquiry
		});
	};

	private handleReplyDialogCancel = () => {
		this.setState({
			replyingInquiry: null
		});
	};

	private handleReplyDialogSend = (message: string) => {
		addInquiryMessageAndUpdateStore(this.state.replyingInquiry.id, message);

		this.setState({
			replyingInquiry: null
		});
	};

	private renderShowBookingPlanButton = (ts: string) => {
		const { classes, layout } = this.props;

		if(layout === "phone") {
			return ( 
				<div>
					<IconButton
						color="inherit"
						className={classNames(
							classes.button,
							classes.buttonGap
						)}
						onClick={() => this.handleShowDashboardButtonClick(ts)}
					>
						<AssessmentIcon />
					</IconButton>
					<IconButton
						color="inherit"
						className={classes.button}
						onClick={() => this.handleShowBookingPlanButtonClick(ts)}
					>
						<ListIcon />
					</IconButton>
				</div>  
			);
		}

		return (
			<div>
				<Button
					className={classNames(
						classes.button,
						classes.buttonGap
					)}
					onClick={() => this.handleShowDashboardButtonClick(ts)}
					variant="outlined"
				>
					Tagesübersicht
				</Button>
				<Button
					className={classes.button}
					onClick={() => this.handleShowBookingPlanButtonClick(ts)}
					variant="outlined"
				>
					Belegung anzeigen
				</Button>
			</div>
		);
	};

	private renderInquiries = (inquries: Inquiry[]) => {
		const { highlightedInquiry } = this.props;

		return inquries.map(inquiry => (
			<ConnectedInquiry
				key={inquiry.id}
				data={inquiry}
				payment={inquiry.payment}
				highlighted={
					inquiry.id ===
					highlightedInquiry
				}
				onReply={this.handleReply}
				{...inquiry}
			/>
		));
	};

	public render() {
		const { classes, layout } = this.props;
		const {
			groupedInquiries,
			showBookingPlan,
			replyingInquiry
		} = this.state;

		const timestamps = Object.keys(groupedInquiries);

		return (
			<div className={classes.root}>
				<ConnectedSheet
					maxWidth={1200}
					paddingLeft={0}
					paddingTop={0}
					paddingRight={0}
					paddingBottom={0}
				>
					{timestamps.length > 0 ? (
						timestamps.map(ts => (
							<ConnectedListSection
								key={ts}
								title={formatDate(parseInt(ts))}
								actions={this.renderShowBookingPlanButton(ts)}
							>
								{this.renderInquiries(groupedInquiries[parseInt(ts)].default)}
								{this.renderInquiries(groupedInquiries[parseInt(ts)].snoozed)}
							</ConnectedListSection>
						))
					) : (
						<ConnectedListSection title="Keine Anfragen vorhanden!" />
					)}
					{layout === "phone" ? (
						<ConnectedBookingListDialog
							open={showBookingPlan}
							onClose={this.handleBookingPlanDrawerClose}
						/>
					) : (
						<ConnectedBookingPlanDrawer
							open={showBookingPlan}
							onClose={this.handleBookingPlanDrawerClose}
						/>
					)}
					<ConnectedSendMessageDialog
						open={Boolean(replyingInquiry)}
						inquiry={replyingInquiry}
						onCancel={this.handleReplyDialogCancel}
						onSend={this.handleReplyDialogSend}
					/>
				</ConnectedSheet>
			</div>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	areas: state.areas,
	selectedDate: state.date,
	activeAreaId: state.overviewActiveAreaId,
	inquiries: state.inquiries,
	archivedInquiries: state.archivedInquiries,
	archivedInquiriesVisible: state.archivedInquiriesVisible,
	dateRange: state.inquiryListDateRange,
	highlightedInquiry: state.inquiryListHighlightedInquiry,
	layout: state.layout
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	setDate: (date: Date) => dispatch(setDate(date)),
	setActiveArea: (id: string) => dispatch(setActiveArea(id))
});

const styles = (theme: Theme) =>
	createStyles({
		root: {
			flexGrow: 1
		},
		button: {
			textTransform: "none"
		},
		buttonGap: {
			marginRight: 8
		}
	});

export const ConnectedInquiryList = withRouter(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(InquiryList)
	)
);
